/* Newsroom.

   Independent enhancements. All of them are additive: without this file
   every story is visible and every link still works. */

/* ---------- Category filtering (index) ---------- */
(() => {
  const filters = document.querySelectorAll('[data-nr-filter]');
  const results = document.querySelector('[data-nr-results]');
  if (!filters.length || !results) return;

  const posts = results.querySelectorAll('[data-nr-category]');
  const empty = results.querySelector('[data-nr-empty]');

  const apply = (category) => {
    let shown = 0;

    posts.forEach((post) => {
      const match = category === 'all' || post.dataset.nrCategory === category;
      post.classList.toggle('is-hidden', !match);
      if (match) shown += 1;
    });

    // section labels and row wrappers would otherwise sit above nothing
    results.querySelectorAll('.nr-section-label, .nr-grid').forEach((group) => {
      const owned = group.classList.contains('nr-grid')
        ? group.querySelectorAll('[data-nr-category]:not(.is-hidden)').length
        : 1;
      if (group.classList.contains('nr-grid')) {
        group.hidden = owned === 0;
      }
    });

    const label = results.querySelector('.nr-section-label');
    const grid3 = results.querySelector('.nr-grid--3');
    if (label && grid3) label.hidden = grid3.hidden;

    if (empty) empty.hidden = shown !== 0;
  };

  filters.forEach((btn) => {
    btn.addEventListener('click', () => {
      filters.forEach((b) => b.classList.toggle('is-active', b === btn));
      apply(btn.dataset.nrFilter);
    });
  });
})();

/* ---------- Section nav (article) ----------
   Built from the rendered H2s rather than authored separately, so an
   admin-written article body gets a nav with no extra work and the two
   can never drift apart. */
(() => {
  const toc = document.querySelector('[data-nr-toc]');
  const body = document.querySelector('.nr-body');
  if (!toc || !body) return;

  // direct children only: the About and Press contact headings live
  // inside .nr-endmatter and are not article sections
  const headings = [...body.querySelectorAll(':scope > h2')];
  if (headings.length < 2) return;

  const slug = (text, i) =>
    text.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || `section-${i}`;

  const links = headings.map((h, i) => {
    if (!h.id) h.id = slug(h.textContent, i);
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.className = 'nr-toc__link';
    a.href = `#${h.id}`;
    a.textContent = h.textContent.trim();
    li.appendChild(a);
    return { heading: h, link: a, li };
  });

  const list = document.createElement('ol');
  list.className = 'nr-toc__list';
  links.forEach((l) => list.appendChild(l.li));

  const label = document.createElement('p');
  label.className = 'nr-toc__label';
  label.textContent = toc.dataset.nrTocLabel || 'In this release';

  const inner = document.createElement('div');
  inner.className = 'nr-toc__inner';
  inner.append(label, list);
  toc.appendChild(inner);
  toc.hidden = false;

  const setCurrent = (idx) =>
    links.forEach((l, i) => l.link.classList.toggle('is-current', i === idx));

  setCurrent(0);

  // mark the last heading that has passed the top offset, which tracks
  // reading position better than "whichever heading is on screen"
  const OFFSET = 120;
  let ticking = false;

  const update = () => {
    ticking = false;
    let idx = 0;
    links.forEach((l, i) => {
      if (l.heading.getBoundingClientRect().top <= OFFSET) idx = i;
    });
    setCurrent(idx);
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true }
  );

  update();
})();

/* ---------- Ball flight explainer (article) ----------
   Pure decoration on markup that already reads correctly: the reveal
   styles only apply once this file adds .is-ready, so with JS off the
   card renders as plain content. */
(() => {
  const root = document.querySelector('[data-nr-shots]');
  if (!root) return;

  root.classList.add('is-ready');

  // fires once per element when it scrolls in, and runs immediately on
  // browsers with no observer so nothing stays in its pre-reveal state
  const onceVisible = (elements, fn, options) => {
    const pending = new Set(elements);

    const done = (el) => {
      if (pending.delete(el)) fn(el);
    };

    if (!('IntersectionObserver' in window)) {
      elements.forEach(done);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        done(entry.target);
      });
    }, options);

    elements.forEach((el) => observer.observe(el));

    // an anchor jump can take an element from below the viewport to above it
    // in one go, which is not a state change the observer reports, so a
    // scrolled-past element would sit in its hidden state forever
    let ticking = false;

    const sweep = () => {
      ticking = false;
      pending.forEach((el) => {
        if (el.getBoundingClientRect().bottom > 0) return;
        observer.unobserve(el);
        done(el);
      });
      if (!pending.size) window.removeEventListener('scroll', onScroll);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(sweep);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* --- reveal on scroll --- */
  onceVisible([...root.querySelectorAll('.nr-reveal')], (el) => el.classList.add('is-in'), {
    rootMargin: '0px 0px -12% 0px',
    threshold: 0.15,
  });
})();

/* ---------- Copy text of this release (article) ---------- */
(() => {
  const btn = document.querySelector('[data-copy-release]');
  if (!btn) return;

  const source = document.querySelector('[data-release-text]');
  if (!source || !navigator.clipboard) return;

  // only reveal the button once we know the clipboard path exists
  btn.classList.add('is-ready');

  const label = btn.querySelector('[data-copy-label]');
  const idle = label ? label.textContent : '';
  let resetTimer = null;

  // strip the DOM back to the plain text a journalist would paste:
  // headings and paragraphs as blocks, list items as dashes. The ball
  // flight card sits inside the body but is not part of the release, so
  // its copy is skipped, as is the product card until it is docked.
  const releaseText = () => {
    const parts = [];
    source.querySelectorAll('h1, h2, h3, p, li, blockquote').forEach((node) => {
      if (node.closest('.nr-shots')) return;
      const text = node.textContent.trim().replace(/\s+/g, ' ');
      if (text) parts.push(node.tagName === 'LI' ? `- ${text}` : text);
    });
    return parts.join('\n\n');
  };

  btn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(releaseText());
    } catch (e) {
      return; // clipboard denied, leave the button untouched
    }

    btn.classList.add('is-done');
    if (label) label.textContent = 'Copied';

    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => {
      btn.classList.remove('is-done');
      if (label) label.textContent = idle;
    }, 2200);
  });
})();

/* ---------- Share row (article) ----------
   The anchors work on their own; this only lights up the two chips
   that need browser APIs, following the nr-copy pattern. */
(() => {
  document.querySelectorAll('[data-nr-share]').forEach((bar) => {
    const url = bar.dataset.shareUrl;
    const title = bar.dataset.shareTitle;
    if (!url) return;

    const native = bar.querySelector('[data-nr-share-native]');
    if (native && navigator.share) {
      native.classList.add('is-ready');
      native.addEventListener('click', () => {
        navigator.share({ title, url }).catch(() => {});
      });
    }

    const copy = bar.querySelector('[data-nr-share-copy]');
    if (copy && navigator.clipboard) {
      copy.classList.add('is-ready');
      const label = copy.querySelector('[data-copy-link-label]');
      const idle = label ? label.textContent : '';
      let resetTimer = null;

      copy.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(url);
        } catch (e) {
          return; // clipboard denied, leave the button untouched
        }

        copy.classList.add('is-done');
        if (label) label.textContent = 'Copied';

        clearTimeout(resetTimer);
        resetTimer = setTimeout(() => {
          copy.classList.remove('is-done');
          if (label) label.textContent = idle;
        }, 2200);
      });
    }
  });
})();

/* ---------- Ask AI menu ----------
   details/summary opens and closes on its own; this adds the expected
   dismissals: a click anywhere else, or Escape. */
(() => {
  const asks = [...document.querySelectorAll('details[data-nr-ask]')];
  if (!asks.length) return;

  document.addEventListener('click', (event) => {
    asks.forEach((menu) => {
      if (menu.open && !menu.contains(event.target)) menu.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') asks.forEach((menu) => (menu.open = false));
  });
})();
